// eslint-disable-next-line no-unused-vars
import React, { memo, useEffect, useMemo, useState } from 'react';
import { bool } from 'prop-types';

import OffersMenuComponent from 'Components/Offers/OffersMenu';
import ProductOfferItem from 'Containers/Products/ProductOfferItem';
import useAnalytics from 'Hooks/useAnalytics';
import useFetch from 'Hooks/useFetch';
import useOffers from 'Hooks/useOffers';
import { API_OFFERS } from 'Constants';
import isOfferExpired, { clampMarkupPercent } from 'Utils/offers';

const getProductId = product => product.productId || product.id;

const getSavedOffer = response => (
  response?.offer
  || response?.data?.offer
  || response?.data
  || response
);

const getSavedOffers = response => {
  if (Array.isArray(response)) return response;
  return response?.items
    || response?.offers
    || response?.data?.items
    || response?.data?.offers
    || response?.data
    || [];
};

const getItemPayload = product => ({
  productId: String(getProductId(product)),
  quantity: Number(product.quantity || product.minQuantity || 1),
  markupPercent: product.markupPercent === null || product.markupPercent === undefined
    ? null
    : clampMarkupPercent(product.markupPercent),
  unitCustomPrice: product.unitCustomPrice ?? null
});

const getOfferPayload = offer => ({
  name: offer.name,
  clientName: offer.clientName,
  clientEmail: offer.clientEmail,
  currencyId: offer.currencyId,
  items: offer.items.map(getItemPayload),
  defaultMarkupPercent: clampMarkupPercent(offer.defaultMarkupPercent),
  validUntil: offer.validUntil || null
});

const mergeOfferItems = (serverOffer, localItems) => ({
  ...serverOffer,
  items: (serverOffer.items || localItems).map(serverItem => ({
    ...localItems.find(
      item => String(getProductId(item)) === String(getProductId(serverItem))
    ),
    ...serverItem
  }))
});

const OffersMenu = memo(({ active, open }) => {
  const analytics = useAnalytics();
  const fetcher = useFetch();
  const {
    offers: privateOffers,
    activeOfferId,
    activeOfferStorage,
    isHydrated,
    removeProductFromOffer,
    setActiveOffer
  } = useOffers();
  const [savedOffers, setSavedOffers] = useState([]);
  const [savedOffer, setSavedOffer] = useState(null);
  const [listLoading, setListLoading] = useState(false);
  const [savedOffersLoaded, setSavedOffersLoaded] = useState(false);
  const [offerLoading, setOfferLoading] = useState(false);
  const [error, setError] = useState(null);
  const [previewOffer, setPreviewOffer] = useState(null);

  useEffect(() => {
    if (!active || !open) {
      setSavedOffersLoaded(false);
      return undefined;
    }

    let isActive = true;
    setListLoading(true);
    setSavedOffersLoaded(false);
    setError(null);
    fetcher(API_OFFERS)
      .then(response => {
        if (!isActive) return;
        const responseOffers = getSavedOffers(response);
        setSavedOffers(Array.isArray(responseOffers) ? responseOffers : []);
        setSavedOffersLoaded(true);
      })
      .catch(fetchError => {
        if (isActive && fetchError?.code !== 20) {
          setError(fetchError?.info?.errors?.at(0)?.message || fetchError.message);
        }
      })
      .finally(() => {
        if (isActive) setListLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [active, fetcher, open]);

  useEffect(() => {
    if (!active || !open) return;
    analytics.track('mini_offers_viewed', {
      active_offer_id: activeOfferId,
      active_offer_storage: activeOfferStorage
    });
  }, [active, activeOfferId, activeOfferStorage, analytics, open]);

  const offerOptions = useMemo(() => privateOffers
    .map(offer => ({
      id: `private:${ offer.id }`,
      offerId: offer.id,
      label: offer.name,
      storage: 'private',
      expired: isOfferExpired(offer)
    }))
    .concat(savedOffers
      .map(offer => ({
        id: `saved:${ offer.id }`,
        offerId: offer.id,
        label: offer.name,
        storage: 'saved',
        expired: isOfferExpired(offer)
      }))), [privateOffers, savedOffers]);

  const activeOfferSelection = activeOfferId && activeOfferStorage
    ? `${ activeOfferStorage }:${ activeOfferId }`
    : null;
  const selectedOffer = previewOffer || activeOfferSelection;
  const selectedOption = offerOptions.find(option => option.id === selectedOffer);

  useEffect(() => {
    if (!open) setPreviewOffer(null);
  }, [open]);

  useEffect(() => {
    if (previewOffer && !offerOptions.some(option => option.id === previewOffer)) {
      setPreviewOffer(null);
    }
  }, [offerOptions, previewOffer]);

  useEffect(() => {
    if (
      !active
      || !open
      || !isHydrated
      || !savedOffersLoaded
      || listLoading
      || !offerOptions.length
      || activeOfferId
      || selectedOption
    ) return;
    const firstOffer = offerOptions.find(option => !option.expired);
    if (!firstOffer) return;
    setActiveOffer(firstOffer.offerId, firstOffer.storage);
  }, [
    active,
    activeOfferId,
    isHydrated,
    listLoading,
    offerOptions,
    open,
    savedOffersLoaded,
    selectedOption,
    setActiveOffer
  ]);

  useEffect(() => {
    if (!active || !open || selectedOption?.storage !== 'saved') {
      setSavedOffer(null);
      setOfferLoading(false);
      return undefined;
    }

    let isActive = true;
    setOfferLoading(true);
    setSavedOffer(null);
    setError(null);
    fetcher(`${ API_OFFERS }/${ encodeURIComponent(selectedOption.offerId) }`)
      .then(response => {
        if (isActive) setSavedOffer(getSavedOffer(response));
      })
      .catch(fetchError => {
        if (isActive && fetchError?.code !== 20) {
          setError(fetchError?.info?.errors?.at(0)?.message || fetchError.message);
        }
      })
      .finally(() => {
        if (isActive) setOfferLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [
    active,
    fetcher,
    open,
    selectedOption?.id,
    selectedOption?.offerId,
    selectedOption?.storage
  ]);

  const privateOffer = selectedOption?.storage === 'private'
    ? privateOffers.find(offer => String(offer.id) === String(selectedOption.offerId))
    : null;
  const activeOffer = selectedOption?.storage === 'saved' ? savedOffer : privateOffer;
  const offerUrl = selectedOption && (selectedOption.storage === 'private'
    ? `/offers/private/${ selectedOption.offerId }/editor`
    : `/offers/${ selectedOption.offerId }/editor`);

  const handleOfferChange = ({ selectedItem }) => {
    if (!selectedItem) return;
    if (selectedItem.expired) {
      setPreviewOffer(selectedItem.id);
    } else {
      setPreviewOffer(null);
      setActiveOffer(selectedItem.offerId, selectedItem.storage);
    }
    analytics.track('mini_offer_selected', {
      offer_id: selectedItem.offerId,
      storage: selectedItem.storage,
      expired: selectedItem.expired
    });
  };

  const handleRemoveProduct = async product => {
    if (!activeOffer || !selectedOption || isOfferExpired(activeOffer)) return;
    const productId = getProductId(product);
    if (selectedOption.storage === 'private') {
      removeProductFromOffer(activeOffer.id, productId);
      return;
    }

    const updatedItems = activeOffer.items.filter(
      item => String(getProductId(item)) !== String(productId)
    );
    const updatedOffer = { ...activeOffer, items: updatedItems };
    const response = await fetcher(
      `${ API_OFFERS }/${ encodeURIComponent(activeOffer.id) }`,
      {
        method: 'PUT',
        body: JSON.stringify(getOfferPayload(updatedOffer))
      }
    );
    let serverOffer = getSavedOffer(response);
    if (!serverOffer?.items) {
      serverOffer = getSavedOffer(await fetcher(
        `${ API_OFFERS }/${ encodeURIComponent(activeOffer.id) }`
      ));
    }
    setSavedOffer(mergeOfferItems(serverOffer, updatedItems));
    setSavedOffers(currentOffers => currentOffers.map(offer => (
      String(offer.id) === String(activeOffer.id)
        ? { ...offer, numberOfProducts: updatedItems.length }
        : offer
    )));
  };

  const productItems = (activeOffer?.items || []).map(product => (
    <ProductOfferItem
      key={ getProductId(product) }
      offer={ activeOffer }
      product={ product }
      storage={ selectedOption?.storage }
      disabled={ isOfferExpired(activeOffer) }
      onRemove={ handleRemoveProduct }
    />
  ));

  return (
    <OffersMenuComponent
      offers={ offerOptions }
      selectedOffer={ selectedOffer }
      activeOffer={ activeOffer }
      storage={ selectedOption?.storage }
      offerUrl={ offerUrl }
      loading={ !isHydrated || listLoading || offerLoading }
      error={ error }
      onOfferChange={ handleOfferChange }
    >
      { productItems }
    </OffersMenuComponent>
  );
});

OffersMenu.propTypes = {
  active: bool,
  open: bool
};

OffersMenu.displayName = 'OffersMenuContainer';

export default OffersMenu;
